import React, { createContext, useContext, useReducer, useEffect } from 'react'
import apiService from '../services/api'

// Initial state
const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  backendAvailable: true,
  vehicleUsageToday: null
}

// Action types
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  SET_LOADING: 'SET_LOADING',
  CLEAR_ERROR: 'CLEAR_ERROR',
  UPDATE_USER: 'UPDATE_USER',
  SET_BACKEND_STATUS: 'SET_BACKEND_STATUS',
  SET_VEHICLE_USAGE_TODAY: 'SET_VEHICLE_USAGE_TODAY'
}

// Reducer
function authReducer(state, action) {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        isLoading: true,
        error: null
      }
    
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null
      }
    
    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload
      }
    
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      }
    
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      }
    
    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      }
    
    case AUTH_ACTIONS.UPDATE_USER:
      return {
        ...state,
        user: { ...state.user, ...action.payload }
      }
    
    case AUTH_ACTIONS.SET_BACKEND_STATUS:
      return {
        ...state,
        backendAvailable: action.payload
      }
    
    case AUTH_ACTIONS.SET_VEHICLE_USAGE_TODAY:
      return {
        ...state,
        vehicleUsageToday: action.payload || null
      }
    
    default:
      return state
  }
}

// Create context
const AuthContext = createContext()

// Auth provider component
export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState)

  const enrichUser = async (u) => {
    try {
      if (!u) return u
      const assignedId = u?.assignedBranch?.id ?? u?.assignedBranchId ?? u?.branchId ?? null
      let found = null
      try {
        const list = await apiService.admin.getBranches()
        if (Array.isArray(list) && list.length > 0) {
          if (assignedId != null) {
            found = list.find(b => b.id === assignedId) || null
          }
          if (!found && u?.empresa != null) {
            const codStr = String(u.empresa)
            found = list.find(b => String(b.codigo) === codStr) || null
          }
        }
      } catch {}
      if (found) {
        const prev = u.assignedBranch || {}
        const assigned = {
          ...prev,
          id: found.id ?? prev.id,
          name: found.name ?? prev.name,
          sigla: (found.sigla ?? prev.sigla ?? prev.acronym),
          codigo: (found.codigo ?? prev.codigo ?? u.empresa ?? null)
        }
        return { ...u, assignedBranch: assigned }
      } else {
        if (u.assignedBranch && (u.assignedBranch.codigo == null) && u.empresa != null) {
          const assigned = { ...u.assignedBranch, codigo: u.empresa }
          return { ...u, assignedBranch: assigned }
        }
      }
      return u
    } catch {
      return u
    }
  }

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem('authToken')
        const userData = localStorage.getItem('user')
        const logoutFlag = localStorage.getItem('explicitLogout')
        
        
        // If user explicitly logged out, don't auto-login
        if (logoutFlag === 'true') {
          localStorage.removeItem('explicitLogout')
          dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false })
          return
        }
        
        if (token && userData) {
          try {
            const user = JSON.parse(userData)
            
            // Always verify token with server - don't allow offline access
            try {
              const currentUser = await apiService.users.getProfile()

              dispatch({
                type: AUTH_ACTIONS.LOGIN_SUCCESS,
                payload: { user: currentUser, token }
              })
            } catch (apiError) {
              localStorage.removeItem('authToken')
              //localStorage.removeItem('user')
              dispatch({ type: AUTH_ACTIONS.LOGOUT })
              dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false })
              const msg = String(apiError?.message || '')
              const unauthorized = apiError?.status === 401 || apiError?.name === 'UnauthorizedError' || msg.includes('Authentication failed')
              if (!unauthorized) {
                dispatch({ type: AUTH_ACTIONS.SET_BACKEND_STATUS, payload: false })
              }
            }
          } catch (parseError) {
            // If we can't parse user data, clear storage
            console.error('Error parsing user data:', parseError)
            localStorage.removeItem('authToken')
            localStorage.removeItem('user')
            dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false })
          }
        } else {
          dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false })
        }
      } catch (error) {
        console.error('Auth initialization error:', error)
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false })
      }
    }

    initializeAuth()
  }, [])

  useEffect(() => {
    const fixBranchCodigo = async () => {
      const u = state.user
      if (!u) return
      const hasCodigo = u?.assignedBranch?.codigo != null
      if (hasCodigo) return
      const enriched = await enrichUser(u)
      if (enriched?.assignedBranch?.codigo != null) {
        dispatch({ type: AUTH_ACTIONS.UPDATE_USER, payload: enriched })
      }
    }
    fixBranchCodigo()
  }, [state.user])

  // Helper para formatar data local YYYY-MM-DD
  const toLocalYYYYMMDD = (date) => {
    try {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    } catch {
      return '';
    }
  }

  // Atualiza o uso de veículo do dia (aberto ou fechado)
  const refreshVehicleUsageToday = async () => {
    try {
      const codigo = state?.user?.codigo;
      if (!codigo) {
        dispatch({ type: AUTH_ACTIONS.SET_VEHICLE_USAGE_TODAY, payload: null })
        return null
      }

      const hoje = new Date(); hoje.setHours(0, 0, 0, 0)
      const diaLocal = toLocalYYYYMMDD(hoje)

      let usoAtual = null
      try {
        const usosUsuario = await apiService.veiculoUso.list({ usuarioCodigo: String(codigo) })
        if (Array.isArray(usosUsuario) && usosUsuario.length > 0) {
          usoAtual = usosUsuario.find((u) => !u.dtFim) || usosUsuario.find((u) => {
            try {
              const dtStr = String(u.dtUso || u.dtInicio || '').substring(0, 10)
              return dtStr === diaLocal
            } catch {
              return false
            }
          }) || null
        }

        if (!usoAtual) {
          const usosDoDia = await apiService.veiculoUso.list({ usuarioCodigo: String(codigo), dia: diaLocal })
          if (Array.isArray(usosDoDia) && usosDoDia.length > 0) {
            usoAtual = usosDoDia[0]
          }
        }
      } catch (err) {
        console.error('Falha ao buscar veiculoUso do dia:', err)
      }

      dispatch({ type: AUTH_ACTIONS.SET_VEHICLE_USAGE_TODAY, payload: usoAtual })
      return usoAtual
    } catch (error) {
      dispatch({ type: AUTH_ACTIONS.SET_VEHICLE_USAGE_TODAY, payload: null })
      return null
    }
  }

  // Buscar uso do veículo quando usuário autenticar ou mudar
  useEffect(() => {
    if (state.isAuthenticated && state.user?.codigo) {
      refreshVehicleUsageToday()
    } else {
      dispatch({ type: AUTH_ACTIONS.SET_VEHICLE_USAGE_TODAY, payload: null })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isAuthenticated, state.user?.codigo])

  // Login function
  const login = async (credentials) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START })
    
    try {
      const response = await apiService.auth.login(credentials)
      const { user, access_token } = response
      
      // Store in localStorage
      localStorage.setItem('authToken', access_token)
      localStorage.setItem('user', JSON.stringify(user))
      
      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { user, token: access_token }
      })
      
      return { success: true, user }
    } catch (error) {
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: error.message
      })
      return { success: false, error: error.message }
    }
  }

  // Register function
  const register = async (userData) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START })
    
    try {
      const response = await apiService.auth.register(userData)
      const { user, access_token, emailSent } = response
      
      // Store in localStorage
      localStorage.setItem('authToken', access_token)
      localStorage.setItem('user', JSON.stringify(user))
      
      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { user, token: access_token }
      })
      
      return { success: true, user, emailSent }
    } catch (error) {
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: error.message
      })
      return { success: false, error: error.message }
    }
  }

  // Logout function
  const logout = async () => {

    try {
      await apiService.auth.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      // Set explicit logout flag before clearing storage

      localStorage.setItem('explicitLogout', 'true')
      
      // Clear localStorage

      localStorage.removeItem('authToken')
      localStorage.removeItem('user')

      
      dispatch({ type: AUTH_ACTIONS.LOGOUT })

    }
  }

  // Funcionalidade removida - campo email não existe mais
  /*
  const forgotPassword = async (email) => {
    try {
      await apiService.auth.forgotPassword(email)
      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }
  */

  // Reset password function
  const resetPassword = async (token, password) => {
    try {
      await apiService.auth.resetPassword(token, password)
      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  // Update user profile
  const updateProfile = async (userData) => {
    try {
      const updatedUser = await apiService.users.updateProfile(userData)
      
      // Update localStorage
      localStorage.setItem('user', JSON.stringify(updatedUser))
      
      dispatch({
        type: AUTH_ACTIONS.UPDATE_USER,
        payload: updatedUser
      })
      
      return { success: true, user: updatedUser }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  // Change password function
  const changePassword = async (passwordData) => {
    try {
      await apiService.users.changePassword(passwordData)
      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  // Clear error
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR })
  }

  // Set backend status
  const setBackendStatus = (available) => {
    dispatch({ type: AUTH_ACTIONS.SET_BACKEND_STATUS, payload: available })
  }

  // Handle backend available
  const handleBackendAvailable = async () => {
    // Marca backend como disponível novamente
    setBackendStatus(true)

    // Se houve logout explícito, não tenta revalidar
    const logoutFlag = localStorage.getItem('explicitLogout')
    if (logoutFlag === 'true') {
      return
    }

    // Se há token e usuário autenticado, tenta revalidar perfil sem recarregar a página
    const token = localStorage.getItem('authToken')
    if (token && state.isAuthenticated) {
      try {
        const currentUser = await apiService.users.getProfile()
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { user: currentUser, token }
        })
        // Atualiza dados derivados que dependem do usuário
        try {
          await refreshVehicleUsageToday()
        } catch (e) {
          console.warn('Falha ao atualizar uso de veículo após reconexío:', e?.message || e)
        }
        console.log('✅ Backend disponível e usuário revalidado sem recarregar')
      } catch (apiError) {
        // Se falhar, mantém usuário deslogado e deixa que a UI conduza
        console.warn('Falha ao revalidar perfil após reconexío:', apiError?.message || apiError)
        dispatch({ type: AUTH_ACTIONS.LOGOUT })
      }
    } else {
      console.log('ℹ️ Sem token ou não autenticado; apenas retomando UI sem recarregar')
    }
  }

  // Context value
  const value = {
    ...state,
    login,
    register,
    logout,
    // forgotPassword, // Removido
    resetPassword,
    updateProfile,
    changePassword,
    clearError,
    setBackendStatus,
    handleBackendAvailable,
    vehicleUsageToday: state.vehicleUsageToday,
    refreshVehicleUsageToday,
    branches: { codigo: state?.user?.assignedBranch?.codigo ?? state?.user?.empresa ?? null }
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

// Custom hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthContext